import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { BoostrSearch } from '../components/BoostrSearch';
import {
  Search,
  ArrowLeft,
  Building2,
  Scale,
  User,
  Calendar,
  Clock,
  FileText,
  ChevronRight,
  ChevronDown,
  Loader2,
  AlertCircle,
  CheckCircle,
  Filter,
  ExternalLink,
  Gavel,
  Briefcase,
  Users,
  Calculator,
  Info
} from 'lucide-react';

const STATUS_CONFIG = {
  en_tramite: { color: 'blue', label: 'En Trámite', icon: Clock },
  terminada: { color: 'green', label: 'Terminada', icon: CheckCircle },
  archivada: { color: 'slate', label: 'Archivada', icon: FileText },
  suspendida: { color: 'amber', label: 'Suspendida', icon: AlertCircle },
  en_acuerdo: { color: 'purple', label: 'En Acuerdo', icon: Scale }
};

const MATERIA_ICONS = {
  laboral: Briefcase,
  familia: Users,
  tributario: Calculator,
  constitucional: Scale,
  civil: FileText
};

const PoderJudicialPage = () => {
  const { api } = useAuth();
  
  // State
  const [activeTab, setActiveTab] = useState('search'); // search, my-causes
  const [searchType, setSearchType] = useState('nombre'); // nombre, rut, rol, materia
  const [searchValue, setSearchValue] = useState('');
  const [selectedTribunal, setSelectedTribunal] = useState('');
  const [selectedMateria, setSelectedMateria] = useState('');
  const [selectedEstado, setSelectedEstado] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tribunales, setTribunales] = useState([]);
  const [materias, setMaterias] = useState([]);
  const [estados, setEstados] = useState([]);
  const [selectedCausa, setSelectedCausa] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);
  
  const loadInitialData = async () => {
    try {
      const [tribunalesRes, materiasRes, estadosRes] = await Promise.all([
        api.get('/poder-judicial/tribunales'),
        api.get('/poder-judicial/materias'),
        api.get('/poder-judicial/estados')
      ]);
      
      setTribunales(tribunalesRes.data.tribunales || []);
      setMaterias(materiasRes.data.materias || []);
      setEstados(estadosRes.data.estados || []);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };
  
  const handleSearch = async () => {
    if (!searchValue.trim() && !selectedMateria && !selectedTribunal) {
      toast.error('Ingresa un criterio de búsqueda');
      return;
    }
    
    setLoading(true);
    setSelectedCausa(null);
    
    try {
      const params = new URLSearchParams();
      
      if (searchValue) {
        if (searchType === 'rut') {
          params.append('rut', searchValue);
        } else if (searchType === 'rol') {
          params.append('rol', searchValue);
        } else {
          params.append('nombre', searchValue);
        }
      }
      
      if (selectedTribunal) params.append('tribunal', selectedTribunal);
      if (selectedMateria) params.append('materia', selectedMateria);
      if (selectedEstado) params.append('estado', selectedEstado);
      params.append('limit', '20');
      
      const response = await api.get(`/poder-judicial/search?${params.toString()}`);
      setResults(response.data.results || []);
      
      if (response.data.results?.length === 0) {
        toast.info('No se encontraron causas con esos criterios');
      } else {
        toast.success(`${response.data.results.length} causas encontradas`);
      }
    } catch (error) {
      console.error('Error searching:', error);
      toast.error(error.response?.data?.detail || 'Error en la búsqueda');
    } finally {
      setLoading(false);
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  
  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(amount);
  };
  
  return (
    <div className="min-h-screen bg-background" data-testid="poder-judicial-page">
      {/* Header */}
      <header className="bg-card border-b border-border px-4 py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-lg">Consulta de Causas</h1>
                <p className="text-xs text-slate-400">Poder Judicial de Chile</p>
              </div>
            </div>
          </div>
          
          <a
            href="https://www.pjud.cl"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            pjud.cl
          </a>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 py-6">
        <BoostrSearch />
        {/* Search Section */}
        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <div className="flex flex-col gap-4">
            {/* Search Type Tabs */}
            <div className="flex gap-2 border-b border-slate-700 pb-3">
              {[
                { id: 'nombre', label: 'Por Nombre', icon: User },
                { id: 'rut', label: 'Por RUT', icon: FileText },
                { id: 'rol', label: 'Por ROL', icon: Scale },
                { id: 'materia', label: 'Por Materia', icon: Gavel }
              ].map(type => (
                <button
                  key={type.id}
                  onClick={() => setSearchType(type.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                    searchType === type.id
                      ? 'bg-blue-500/20 text-blue-400'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <type.icon className="w-4 h-4" />
                  {type.label}
                </button>
              ))}
            </div>
            
            {/* Search Input */}
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder={
                    searchType === 'rut' ? 'Ej: 12.345.678-9' :
                    searchType === 'rol' ? 'Ej: C-12345-2024' :
                    searchType === 'materia' ? 'Ej: despido, alimentos, tributario' :
                    'Nombre del litigante'
                  }
                  data-testid="search-input"
                  className="w-full pl-12 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:border-blue-500 focus:outline-none"
                />
              </div>
              
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-3 rounded-xl border transition-colors flex items-center gap-2 ${
                  showFilters ? 'bg-blue-500/20 border-blue-500 text-blue-400' : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-white'
                }`}
              >
                <Filter className="w-5 h-5" />
                Filtros
              </button>
              
              <button
                onClick={handleSearch}
                disabled={loading}
                data-testid="search-btn"
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-xl font-medium transition-colors flex items-center gap-2"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                Buscar
              </button>
            </div>
            
            {/* Filters */}
            {showFilters && (
              <div className="grid md:grid-cols-3 gap-4 pt-4 border-t border-slate-700">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Tribunal</label>
                  <select
                    value={selectedTribunal}
                    onChange={(e) => setSelectedTribunal(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                  >
                    <option value="">Todos los tribunales</option>
                    {tribunales.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Materia</label>
                  <select
                    value={selectedMateria}
                    onChange={(e) => setSelectedMateria(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                  >
                    <option value="">Todas las materias</option>
                    {materias.map(m => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Estado</label>
                  <select
                    value={selectedEstado}
                    onChange={(e) => setSelectedEstado(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                  >
                    <option value="">Todos los estados</option>
                    {estados.map(e => (
                      <option key={e.id} value={e.id}>{e.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Results */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Results List */}
          <div className="space-y-4">
            {results.length > 0 ? (
              <>
                <h3 className="font-medium text-white">{results.length} causas encontradas</h3>
                
                {results.map((causa) => {
                  const statusConfig = STATUS_CONFIG[causa.estado] || STATUS_CONFIG.en_tramite;
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <button
                      key={causa.id}
                      onClick={() => setSelectedCausa(causa)}
                      className={`w-full text-left bg-card border rounded-xl p-4 transition-all ${
                        selectedCausa?.id === causa.id
                          ? 'border-blue-500 ring-2 ring-blue-500/20'
                          : 'border-border hover:border-slate-600'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-mono text-blue-400 font-medium">{causa.rol}</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium bg-${statusConfig.color}-500/20 text-${statusConfig.color}-400 flex items-center gap-1`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusConfig.label}
                            </span>
                          </div>
                          <h4 className="font-medium text-white mb-1">{causa.caratula}</h4>
                          <p className="text-sm text-slate-400">{causa.materia}</p>
                          <p className="text-xs text-slate-500 mt-1">{causa.tribunal_name}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-500" />
                      </div>
                    </button>
                  );
                })}
              </>
            ) : (
              <div className="bg-card border border-border rounded-xl p-12 text-center">
                <Building2 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-slate-400 mb-2">Consulta de Causas</h4>
                <p className="text-sm text-slate-500 mb-4">
                  Busca causas por nombre, RUT, ROL o materia en los tribunales chilenos
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {['González', 'despido', 'alimentos'].map(term => (
                    <button
                      key={term}
                      onClick={() => { setSearchValue(term); setSearchType('nombre'); }}
                      className="px-3 py-1 bg-slate-700 hover:bg-slate-600 rounded-full text-sm text-slate-300"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Causa Detail */}
          <div>
            {selectedCausa ? (
              <div className="bg-card border border-border rounded-xl overflow-hidden sticky top-24">
                <div className="p-5 border-b border-slate-700 bg-slate-800/50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-xl text-blue-400 font-bold">{selectedCausa.rol}</span>
                    {(() => {
                      const sc = STATUS_CONFIG[selectedCausa.estado] || STATUS_CONFIG.en_tramite;
                      return (
                        <span className={`px-3 py-1 rounded-full text-sm font-medium bg-${sc.color}-500/20 text-${sc.color}-400`}>
                          {sc.label}
                        </span>
                      );
                    })()}
                  </div>
                  <h3 className="font-semibold text-white text-lg">{selectedCausa.caratula}</h3>
                  <p className="text-slate-400">{selectedCausa.tribunal_name}</p>
                </div>
                
                <div className="p-5 space-y-4">
                  {/* Info básica */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-500">Materia</p>
                      <p className="font-medium text-white">{selectedCausa.materia}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Fecha Ingreso</p>
                      <p className="font-medium text-white">{formatDate(selectedCausa.fecha_ingreso)}</p>
                    </div>
                    {selectedCausa.cuantia && (
                      <div>
                        <p className="text-xs text-slate-500">Cuantía</p>
                        <p className="font-medium text-emerald-400">{formatCurrency(selectedCausa.cuantia)}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Partes */}
                  <div className="pt-4 border-t border-slate-700">
                    <h4 className="font-medium text-white mb-3">Partes</h4>
                    <div className="space-y-2">
                      <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                        <p className="text-xs text-emerald-400 mb-1">Demandante</p>
                        <p className="font-medium text-white">{selectedCausa.demandante?.nombre}</p>
                        <p className="text-xs text-slate-400">{selectedCausa.demandante?.rut}</p>
                      </div>
                      <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-lg">
                        <p className="text-xs text-rose-400 mb-1">Demandado</p>
                        <p className="font-medium text-white">{selectedCausa.demandado?.nombre}</p>
                        <p className="text-xs text-slate-400">{selectedCausa.demandado?.rut}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Actuaciones */}
                  {selectedCausa.actuaciones?.length > 0 && (
                    <div className="pt-4 border-t border-slate-700">
                      <h4 className="font-medium text-white mb-3">Últimas Actuaciones</h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {selectedCausa.actuaciones.slice(-5).reverse().map((act, index) => (
                          <div key={index} className="p-3 bg-slate-800/50 rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-blue-400">{act.tipo}</span>
                              <span className="text-xs text-slate-500">{formatDate(act.fecha)}</span>
                            </div>
                            <p className="text-sm text-slate-300">{act.descripcion}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Próxima actuación */}
                  {selectedCausa.proxima_actuacion && (
                    <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-4 h-4 text-amber-400" />
                        <span className="font-medium text-amber-400">Próxima Actuación</span>
                      </div>
                      <p className="text-white font-medium">{selectedCausa.proxima_actuacion.tipo}</p>
                      <p className="text-sm text-slate-400">
                        {formatDate(selectedCausa.proxima_actuacion.fecha)}
                        {selectedCausa.proxima_actuacion.hora && ` a las ${selectedCausa.proxima_actuacion.hora}`}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-xl p-12 text-center sticky top-24">
                <Scale className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-slate-400 mb-2">Detalle de Causa</h4>
                <p className="text-sm text-slate-500">
                  Selecciona una causa para ver sus detalles, partes y actuaciones
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* Info Banner */}
        <div className="mt-6 bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-400 mb-1">Base de Datos de Demostración</h4>
              <p className="text-sm text-slate-400">
                Esta es una versión de demostración. En producción, NotaryOS se conecta a servicios autorizados 
                para consultar causas del Poder Judicial de Chile en tiempo real.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PoderJudicialPage;
